import { getItem, setItem } from './storage';
import { 
  GLOBAL_CHANTER_KEY,
  GLOBAL_CHANTER_ID_KEY,
  GLOBAL_MIC_KEY,
  STANDARD_CHANTER_UUID,
  KEYBOARD_CHANTER_UUID
} from './constants';
import { 
  SCHEMA_VERSION_KEY,
  SCHEMA_VERSION,
  getCurrentVersion, 
  runMigrations, 
  needsMigration,
  completeSetup
} from './migrationPipeline';

// Additional keys for settings management
const MIGRATION_NOTICE_DISMISSED_KEY = 'migration_notice_dismissed';

/**
 * Get the globally selected microphone device ID
 * @returns {string|null} Device ID or null if not set
 */
export function getGlobalMicrophone() {
  return getItem(GLOBAL_MIC_KEY, null);
}

/**
 * Set the globally selected microphone device
 * @param {string} deviceId - The device ID to set
 * @returns {boolean} true if successful
 */
export function setGlobalMicrophone(deviceId) {
  // Remove diagnostic logging
  return setItem(GLOBAL_MIC_KEY, deviceId);
}

/**
 * Get the globally selected chanter
 * @returns {string} Chanter name, defaults to 'Standard'
 */
export function getGlobalChanter() {
  return getItem(GLOBAL_CHANTER_KEY, 'Standard');
}

/**
 * Set the globally selected chanter
 * @param {string} chanterName - The chanter name to set
 * @returns {boolean} true if successful
 */
export function setGlobalChanter(chanterName) {
  // Also update the ID when setting the name
  if (chanterName === 'Standard') {
    setGlobalChanterId(STANDARD_CHANTER_UUID);
  } else if (chanterName === 'Keyboard') {
    setGlobalChanterId(KEYBOARD_CHANTER_UUID);
  } else {
    // For custom chanters, try to find the chanter in the saved chanters list
    const savedChanters = getItem('bagpipe_chanters', []);
    const chanter = savedChanters.find(c => c.name === chanterName);
    if (chanter && chanter.id) {
      setGlobalChanterId(chanter.id);
    }
  }
  
  return setItem(GLOBAL_CHANTER_KEY, chanterName);
}

/**
 * Get the globally selected chanter ID
 * @returns {string} Chanter UUID, defaults to STANDARD_CHANTER_UUID
 */
export function getGlobalChanterId() {
  return getItem(GLOBAL_CHANTER_ID_KEY, STANDARD_CHANTER_UUID);
}

/**
 * Set the globally selected chanter ID
 * @param {string} chanterId - The chanter UUID to set
 * @returns {boolean} true if successful
 */
export function setGlobalChanterId(chanterId) {
  return setItem(GLOBAL_CHANTER_ID_KEY, chanterId);
}

/**
 * Check if the currently selected chanter is a standard (non-custom) chanter
 * @returns {boolean} True if using a standard chanter
 */
export function isUsingStandardChanter() {
  const chanterId = getGlobalChanterId();
  return chanterId === STANDARD_CHANTER_UUID || chanterId === KEYBOARD_CHANTER_UUID;
}

/**
 * Check if the migration notice has been dismissed
 * @returns {boolean} true if the notice has been dismissed
 */
export function isMigrationNoticeDismissed() {
  return getItem(MIGRATION_NOTICE_DISMISSED_KEY, false);
}

/**
 * Dismiss the migration notice
 * @returns {boolean} true if successful
 */
export function dismissMigrationNotice() {
  return setItem(MIGRATION_NOTICE_DISMISSED_KEY, true);
}

/**
 * Check if the application should show a migration notice
 * @returns {boolean} true if a notice should be shown
 */
export function shouldShowMigrationNotice() {
  return needsMigration() && !isMigrationNoticeDismissed();
}

/**
 * Apply component-specific preferences during initial migration
 * This is useful for components that need to keep some preferences separate
 * @param {string} component - Component name
 * @param {Object} preferences - Preferences to save
 * @returns {boolean} true if successful
 */
export function applyComponentPreferences(component, preferences) {
  const key = `${component.toLowerCase()}_preferences`;
  return setItem(key, preferences);
}

/**
 * Get component-specific preferences
 * @param {string} component - Component name
 * @returns {Object} Component preferences
 */
export function getComponentPreferences(component, defaultValue = {}) {
  const key = `${component.toLowerCase()}_preferences`;
  return getItem(key, defaultValue);
}

/**
 * Perform the upgrade and handle notifications
 * @returns {Object} Results of the upgrade
 */
export function performUpgrade() {
  // Run the migrations
  const results = runMigrations();
  
  // Reset the migration notice
  setItem(MIGRATION_NOTICE_DISMISSED_KEY, false);
  
  return results;
}

/**
 * Initialize global settings if needed
 * This should be called when the application starts
 */
export function initializeGlobalSettings() {
  // Check if global settings exist
  const mic = getGlobalMicrophone();
  const chanter = getGlobalChanter();
  
  // Get current schema version
  const version = getCurrentVersion();
  
  console.log(`Initializing global settings. Current version: ${version}`);
  console.log(`Global microphone: ${mic || 'Not set'}`);
  console.log(`Global chanter: ${chanter || 'Not set'}`);
  
  // If we're on the latest version but don't have global settings,
  // something went wrong, so reset to default
  if (version === SCHEMA_VERSION && (!mic && !chanter)) {
    console.warn('Global settings not found but schema version is current. Resetting to defaults.');
    setGlobalMicrophone(null);
    setGlobalChanter('Standard');
  }
  
  // Ensure the chanter ID is set correctly
  ensureChanterId();
  
  return {
    version,
    mic,
    chanter,
    needsMigration: needsMigration()
  };
}

/**
 * Ensure the chanter ID is set correctly based on the chanter name
 * This is used for migration to the UUID-based system
 */
export function ensureChanterId() {
  const chanterName = getGlobalChanter();
  const currentId = getGlobalChanterId();
  
  // If the ID is already set, make sure it's consistent with the name
  if (chanterName === 'Standard' && currentId !== STANDARD_CHANTER_UUID) {
    setGlobalChanterId(STANDARD_CHANTER_UUID);
  } else if (chanterName === 'Keyboard' && currentId !== KEYBOARD_CHANTER_UUID) {
    setGlobalChanterId(KEYBOARD_CHANTER_UUID);
  } else if (chanterName !== 'Standard' && chanterName !== 'Keyboard') {
    // For custom chanters, make sure the ID exists
    const savedChanters = getItem('bagpipe_chanters', []);
    const chanter = savedChanters.find(c => c.name === chanterName);
    if (chanter && chanter.id) {
      setGlobalChanterId(chanter.id);
    }
  }
}

/**
 * Get current settings state
 * @returns {Object} Current settings state
 */
export function getSettingsState() {
  return {
    microphone: getGlobalMicrophone(),
    chanter: getGlobalChanter(),
    chanterId: getGlobalChanterId(),
    version: getCurrentVersion(),
    needsUpgrade: needsMigration(),
    noticeAcknowledged: isMigrationNoticeDismissed()
  };
} 